import { useCallback } from 'react'
import { useIntl } from 'react-intl'

import { cn } from '@/shared/ui'
import { focusRing } from '@/shared/ui/cn'
import { ArrowDown, ArrowUp, UnfoldMore } from '@/shared/ui/icons'

import type { SortState } from './search-state'

/**
 * A column header that sorts the results by its field, from the mockups' `find-search/SortableTh.jsx`.
 * Each press moves it a step: ascending, descending, then not sorted, which gives the screen's own
 * order back. A sorted column shows a filled pill with its arrow and its place in the order; the
 * others show a two-way arrow.
 */
export function SortableHeader(props: {
  readonly label: string
  /** The API sort field. */
  readonly field: string
  /** The sort the viewer chose, which may be by another column; `null` for the screen's own order. */
  readonly sort: SortState | null
  readonly onSort: (sort: SortState | null) => void
  readonly className?: string
}) {
  const intl = useIntl()
  const { field, sort, onSort } = props
  const active = sort?.field === field
  const descending = active && sort.descending
  const step = useCallback(
    () => onSort(!active ? { field, descending: false } : descending ? null : { field, descending: true }),
    [field, active, descending, onSort],
  )
  const state = !active ? 'none' : descending ? 'descending' : 'ascending'

  return (
    // Named by its column alone: the button's name, which says how it sorts, would otherwise become the header's.
    <th scope="col" aria-label={props.label} aria-sort={active ? state : undefined} className={props.className}>
      <button
        type="button"
        onClick={step}
        aria-label={intl.formatMessage({ id: `search.sort.state.${state}` }, { column: props.label })}
        title={intl.formatMessage({ id: `search.sort.next.${state}` })}
        className={cn('inline-flex min-h-6 cursor-pointer items-center gap-1.5 whitespace-nowrap rounded transition hover:text-primary-main', focusRing)}
      >
        {props.label}
        {active ? (
          // Hidden, so the column header's name stays the column's; the button's name says how it sorts.
          <span aria-hidden="true" className="inline-flex h-5 items-center gap-0.5 rounded-full bg-primary-main pl-1 pr-1.5 text-xs font-semibold text-primary-contrast">
            {descending ? <ArrowDown size={14} /> : <ArrowUp size={14} />}
            {/* Its place in the order; the API sorts by one column, so always the first. */}
            {intl.formatNumber(1)}
          </span>
        ) : (
          // A token color, not an opacity, so the idle arrow keeps its tested contrast on every surface.
          <UnfoldMore size={18} className="text-text-secondary" />
        )}
      </button>
    </th>
  )
}
